import { useEffect, useState } from 'react';
import { API_CACHE_TTL, fetchCachedApi } from '@/utils/apiCache';

export interface LastFmData {
  name: string;
  bio: {
    summary: string;
    content: string;
  };
  listeners: string | number;
  playcount: string | number;
  similarArtists: Array<{
    name: string;
    url: string;
    image: string;
  }>;
  tags: string[];
  url: string;
}

interface UseLastFmReturn {
  data: LastFmData | null;
  loading: boolean;
  error: string | null;
}

function useLastFm(artistName: string, dependencies: unknown[] = []): UseLastFmReturn {
  const [data, setData] = useState<LastFmData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!artistName) return;

    const fetchLastFmData = async () => {
      setLoading(true);
      setError(null);

      try {
        const json = await fetchCachedApi<LastFmData>(
          "/.netlify/functions/GetLastFmArtist?artistName=" + encodeURIComponent(artistName),
          {},
          {
            ttlMs: API_CACHE_TTL.ONE_DAY,
            shouldCache: (data) => Boolean(data?.name && !((data as unknown as { error?: string }).error)),
          }
        );
        setData(json);
      } catch (err) {
        console.error('Error fetching Last.fm data:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchLastFmData();
  }, [artistName, ...dependencies]);

  return { data, loading, error };
}

export default useLastFm;
